import { inject, Injectable } from '@angular/core';

import { BehaviorSubject } from 'rxjs';

import { ServerErrorReferenceService } from '@services/server-error-reference/server-error-reference.service';
import { TOAST_DURATION } from '@shared/constants/constants';

const REFERENCE_TOAST_MIN_DURATION = 12000;

import { Toast } from './toast.types';

@Injectable({ providedIn: 'root' })
export class ToastService {
  private readonly toastsSubject = new BehaviorSubject<Toast[]>([]);
  public readonly toasts$ = this.toastsSubject.asObservable();

  private readonly serverErrorReferenceService = inject(ServerErrorReferenceService);

  public open(toast: Toast): void {
    const toastWithReference = this.withServerErrorReference(toast);
    const uniqueToast: Toast = { ...toastWithReference, id: Date.now(), open: true };
    this.toastsSubject.next([...this.toastsSubject.getValue(), uniqueToast]);
    setTimeout(() => this.dismiss(uniqueToast.id), toastWithReference.duration ?? TOAST_DURATION);
  }

  public dismiss(id: string | number): void {
    this.toastsSubject.next(
      this.toastsSubject.getValue().filter(t => t.id !== id),
    );
  }

  private withServerErrorReference(toast: Toast): Toast {
    if (toast.type !== 'error' || toast.referenceCode) {
      return toast;
    }

    const referenceCode = this.serverErrorReferenceService.consumeRecentServerErrorReference();
    if (!referenceCode) {
      return toast;
    }

    return {
      ...toast,
      referenceCode,
      dismissible: true,
      duration: Math.max(toast.duration ?? TOAST_DURATION, REFERENCE_TOAST_MIN_DURATION),
    };
  }
}
